<template>
  <WdsStack v-if="loading" justify-center align-center direction-column gap-md>
    <div class="icon-outer">
      <div class="icon-inner">
        <WdsSpinner indeterminate />
      </div>
    </div>
    <WdsText large>
      {{ $t('Loading approvals...') }}
    </WdsText>
  </WdsStack>

  <div v-else-if="!loading && approvalApprovables.length > 0" class="tw-mt-8">
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
      <div class="tw-space-y-10 tw-col-span-8">
        <ApprovalProgress
          v-for="approvalApprovable in approvalApprovables"
          :key="`approval-approvable-${approvalApprovable.id}`"
          :approvable="approvalApprovable"
          @reload-approvals="reloadApprovalApprovables"
        />
      </div>
      <WdsStack
        v-if="worker"
        class="tw-col-span-4 tw-p-4 tw-border tw-rounded tw-border-solid tw-border-gray-200 tw-h-[112px] tw-cursor-pointer"
        direction-column
        justify-space-between
        @click="detailsSidebarVisible = true"
      >
        <WdsStack direction-column justify-center>
          <WdsText large>{{ $t('Talent') }}</WdsText>
        </WdsStack>

        <div class="tw-flex tw-w-full tw-justify-between tw-items-center">
          <WdsAvatarLabel
            :text="worker.name"
            :initials="worker.initials"
            :path="getAvatarPath(worker.avatar, worker.id)"
            truncated
          >
            <template v-if="worker.dayRate" #subText>
              {{
                $t(':currency :dayRate / day', {
                  currency: worker.currency,
                  dayRate: worker.dayRate,
                })
              }}
            </template>
          </WdsAvatarLabel>
          <WdsIcon name="chevron-right" weight="thick" :size="20" />
        </div>
      </WdsStack>
    </div>
  </div>

  <WdsStack v-else justify-center align-center direction-column gap-md>
    <div class="icon-outer">
      <div class="icon-inner">
        <WdsIcon name="flow" :size="20"></WdsIcon>
      </div>
    </div>
    <WdsText large>
      {{ $t('There are no existing approval') }}
    </WdsText>
    <WdsText secondary>
      {{ $t('Hires that requires approval by you or your team will be listed here') }}
    </WdsText>
  </WdsStack>

  <WdsDrawer
    v-if="worker"
    v-model:show="detailsSidebarVisible"
    position="right"
    width="420px"
    padding="0"
  >
    <WorkerDetailPanel
      :viewer-account-id="accountId"
      :worker-id="worker.id"
      @close="detailsSidebarVisible = false"
    />
  </WdsDrawer>
</template>

<script setup lang="ts">
import { computed, ComputedRef, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsIcon,
  WdsText,
  WdsStack,
  WdsSpinner,
  WdsAvatarLabel,
  WdsDrawer,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ApprovalProgress from '@/components/Workflows/Approvals/Request/ApprovalProgress.vue'
import { Approvable } from '@/components/Workflows/Approvals/approvals-types'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import { graphql } from '@/gql'
import { getAvatarPath } from '@/utils/avatar'

const props = defineProps({
  hireId: {
    type: String,
    required: true,
  },
})

const accountId = computed(() => currentAccount.globalId)

const detailsSidebarVisible = ref(false)

const {
  result,
  loading,
  refetch: reloadApprovalApprovables,
} = useQuery(
  graphql(`
    query ApprovalApprovables($approvable: ID!, $first: Int, $usersFirst: Int!, $usersPage: Int) {
      approvalApprovables(approvable: $approvable, first: $first) {
        data {
          id
          approvalRule {
            id
            approvers {
              ...ApproverGroupFragment
            }
          }
          approval {
            name
            trigger
          }
          approvable {
            ... on Hire {
              # We want to use ...HireApprovableFragment here, but it uses the internalBudget field
              # which is under a feature flag. So for now we'll use the contents of that fragment
              # inline, minus the internalBudget field.
              id

              latestContract {
                ...ApprovableContractFragment
              }

              activeContract {
                ...ApprovableContractFragment
              }

              job {
                id
                url
                currency
                name
                customFieldValues {
                  id
                  displayValue
                  customField {
                    title
                    description
                  }
                }
              }

              worker {
                id
                name
                initials
                avatar
                currency
                dayRate
                avatarPath @client
              }

              recruiter {
                id
                name
                owner {
                  id
                  name
                }
              }
              recruiterManagesWorkers
              recruiterOwnershipDays
              recruiterFee

              status

              company {
                name
              }
            }
          }
          approvalStates {
            actionedBy {
              ...UserFragment
            }
            state
            message
            approver {
              id
              position
            }
            createdAt
          }
          viewerCanAction
        }
      }
    }
  `),
  {
    approvable: props.hireId,
    first: 50,
    usersFirst: 50,
    usersPage: 1,
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

// @ts-ignore It is having issues with some of the deeper fragments used on groups and states. A more in depth refactor of this computed
// ref might be warranted at some point
const approvalApprovables: ComputedRef<Approvable[]> = computed(
  () => result.value?.approvalApprovables?.data || [],
)

const worker: ComputedRef = computed(() => approvalApprovables.value[0]?.approvable?.worker)
</script>
